import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '../../react-auth0-spa';
import { images } from '../../images/images';
import { clearSelfServiceCache } from '../../common/selfServiceSolutions';
import { useResendVerificationEmailMutation } from '../../api/account/accountApiSlice';

// Mirrors the server-side `shortWindowMs` on the resend rate limiter
// (new-backend/admin/src/controllers/accountController.ts). Keeping the
// client cooldown identical means a user who waits out the visible
// countdown won't get silently dropped on the next click.
const RESEND_COOLDOWN_SECONDS = 60;

// Rendered for any authenticated user whose `email_verified` claim is
// still false. Acts as the in-app UX layer for email verification — the
// real gate sits in the Auth0 post-login Action (Layer 1), and the
// backend `registerTenant` should also reject unverified callers
// (Layer 3). This page just makes the unverified state pleasant instead
// of dumping the user on Auth0's generic error screen.
const VerifyEmailPage = () => {
    const { t } = useTranslation();
    const { user, loginWithRedirect, logout } = useAuth0();
    const [checking, setChecking] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [resendConfirmation, setResendConfirmation] = useState<string | null>(null);
    const [resendError, setResendError] = useState<string | null>(null);
    const cooldownTimerRef = useRef<number | null>(null);

    const [resendVerification, { isLoading: resending }] = useResendVerificationEmailMutation();

    // Tick the cooldown down once per second. Separate effect from any
    // resend-triggered logic so it only reacts to `resendCooldown` and
    // never re-enters the API call.
    useEffect(() => {
        if (resendCooldown <= 0) {
            if (cooldownTimerRef.current !== null) {
                window.clearTimeout(cooldownTimerRef.current);
                cooldownTimerRef.current = null;
            }
            return;
        }
        cooldownTimerRef.current = window.setTimeout(() => {
            setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
        }, 1000);
        return () => {
            if (cooldownTimerRef.current !== null) {
                window.clearTimeout(cooldownTimerRef.current);
                cooldownTimerRef.current = null;
            }
        };
    }, [resendCooldown]);

    const handleRecheck = async () => {
        setChecking(true);
        setErrorMessage(null);
        try {
            // Full Auth0 redirect (not iframe-based getTokenSilently) so
            // we re-run the post-login Action with the now-true
            // email_verified claim. Silent auth via iframe is blocked on
            // many browsers (third-party cookie restrictions on
            // localhost especially), which is what caused the earlier
            // "Couldn't refresh your account state" error. The user's
            // Auth0 SSO session is still active so this won't prompt
            // for credentials; it just bounces through Auth0 and lands
            // back here with a fresh token whose Layer-2 gate passes.
            await loginWithRedirect({
                appState: { targetUrl: window.location.pathname },
            });
        } catch (err) {
            setErrorMessage(t('verify_email.error_recheck'));
            setChecking(false);
        }
    };

    const handleResend = async () => {
        // Defensive: button is disabled while cooldown > 0, but guard
        // here too in case the disabled state is bypassed (keyboard
        // focus, devtools, etc.) before our state update lands.
        if (resendCooldown > 0 || resending) return;
        setResendConfirmation(null);
        setResendError(null);
        try {
            await resendVerification().unwrap();
            // The backend returns a constant message regardless of
            // outcome (#954); we surface our own translated copy
            // instead of the raw string so the wording is consistent
            // with the rest of the page.
            setResendConfirmation(t('verify_email.resend_confirmation'));
            setResendCooldown(RESEND_COOLDOWN_SECONDS);
        } catch {
            // Only real network/HTTP errors land here — anything the
            // backend handles (rate-limit, already-verified, missing
            // user) still returns 200. So this branch genuinely means
            // "the request didn't get there."
            setResendError(t('verify_email.resend_error'));
        }
    };

    const handleLogout = () => {
        clearSelfServiceCache();
        logout({ logoutParams: { returnTo: window.location.origin } });
    };

    const resendDisabled = resending || resendCooldown > 0;
    const resendLabel = resendCooldown > 0
        ? t('verify_email.resend_cta_cooldown', { seconds: resendCooldown })
        : t('verify_email.resend_cta');

    return (
        <div className={'min-h-screen bg-[rgb(var(--color-default-background))] flex items-center justify-center px-4'}>
            <div className={'w-full max-w-md'}>
                <div className={'flex flex-col items-center mb-8'}>
                    <img src={images.kolulaLogo} alt={'Kolula'} className={'h-10 mb-6'} />
                    <h1 className={'text-2xl font-semibold text-foreground text-center'}>
                        {t('verify_email.title')}
                    </h1>
                    <p className={'text-sm text-foreground/80 mt-2 text-center max-w-sm'}>
                        {t('verify_email.subtitle', { email: user?.email ?? '' })}
                    </p>
                </div>

                <div className={'bg-text-contrast rounded-[5px] shadow-sm p-6 flex flex-col gap-4'}>
                    <p className={'text-sm text-foreground/70'}>
                        {t('verify_email.instructions')}
                    </p>

                    {errorMessage && (
                        <p className={'text-sm text-red-700 bg-red-50 rounded px-3 py-2'}>
                            {errorMessage}
                        </p>
                    )}

                    <button
                        onClick={handleRecheck}
                        disabled={checking}
                        className={[
                            'w-full bg-primary text-text-buttonContrast rounded px-4 py-2',
                            'text-sm font-medium hover:opacity-90 transition-opacity',
                            'disabled:opacity-50 disabled:cursor-not-allowed',
                        ].join(' ')}
                    >
                        {checking ? t('verify_email.checking') : t('verify_email.recheck_cta')}
                    </button>

                    <div className={'border-t border-foreground/10 pt-4 flex flex-col gap-2'}>
                        <p className={'text-xs text-foreground/60'}>
                            {t('verify_email.resend_hint')}
                        </p>

                        {resendConfirmation && (
                            <p className={'text-sm text-green-700 bg-green-50 rounded px-3 py-2'}>
                                {resendConfirmation}
                            </p>
                        )}
                        {resendError && (
                            <p className={'text-sm text-red-700 bg-red-50 rounded px-3 py-2'}>
                                {resendError}
                            </p>
                        )}

                        <button
                            onClick={handleResend}
                            disabled={resendDisabled}
                            className={[
                                'w-full border border-primary text-primary rounded px-4 py-2',
                                'text-sm font-medium hover:bg-primary/5 transition-colors',
                                'disabled:opacity-50 disabled:cursor-not-allowed',
                            ].join(' ')}
                        >
                            {resendLabel}
                        </button>
                    </div>

                    <button
                        onClick={handleLogout}
                        className={'text-sm text-foreground/60 hover:text-foreground transition-colors'}
                    >
                        {t('verify_email.logout_cta')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default VerifyEmailPage;
